import { useState } from "react";
import { Link } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaTwitter } from "react-icons/fa";
import { FiChevronDown, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import useFetch from "../hook/useFetch.jsx";
import { formatPrice } from "../lib/formatPrice";
import CameraImg from "../img/camera.png";

export default function Hero() {
  // feature the "new" products; arrows cycle through them
  const { data } = useFetch("/products/products?isNewProduct=true");
  const products = Array.isArray(data) ? data : [];
  const [index, setIndex] = useState(0);

  const hasSlides = products.length > 0;
  const featured = hasSlides ? products[index % products.length] : null;

  const prev = () =>
    setIndex((i) => (i - 1 + products.length) % products.length);
  const next = () => setIndex((i) => (i + 1) % products.length);

  const title = featured?.title ?? "Canon EOS — Capture more";
  const image = featured?.image ?? CameraImg;
  const href = featured ? `/product/${featured.slug}` : "/";

  return (
    <section className="relative overflow-hidden bg-canvas">
      {/* giant watermark */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-0 flex items-center justify-center">
        <span className="font-display font-bold text-ink/5 text-[24vw] leading-none tracking-tighter">
          CAMERA
        </span>
      </div>

      {/* social rail */}
      <div className="hidden lg:flex flex-col gap-y-5 absolute left-6 top-1/2 -translate-y-1/2 z-10 text-muted">
        {[FaFacebookF, FaInstagram, FaTwitter].map((Icon, i) => (
          <a
            key={i}
            href="https://joemar-ceneza-e-commerce-camera-haus.netlify.app/"
            className="hover:text-brand transition-colors"
            aria-label="Social link">
            <Icon />
          </a>
        ))}
      </div>

      {/* carousel arrows (only when there's more than one slide) */}
      {products.length > 1 && (
        <div className="hidden lg:flex flex-col gap-y-4 absolute right-6 top-1/2 -translate-y-1/2 z-10">
          <button
            type="button"
            onClick={next}
            aria-label="Next product"
            className="text-muted hover:text-brand transition-colors">
            <FiChevronRight className="text-3xl" />
          </button>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous product"
            className="text-muted hover:text-brand transition-colors">
            <FiChevronLeft className="text-3xl" />
          </button>
        </div>
      )}

      <div className="container mx-auto relative z-[1]">
        <div className="grid lg:grid-cols-2 items-center gap-8 min-h-[78vh] pt-32 lg:pt-0">
          {/* copy */}
          <div className="order-2 lg:order-1 text-center lg:text-left">
            <h1 className="display text-5xl md:text-6xl xl:text-7xl mb-5">
              {title}
            </h1>
            <p className="text-muted max-w-md mx-auto lg:mx-0 mb-6">
              Simply built for photographers who actually shoot — pro-grade gear,
              honest prices.
            </p>
            {featured && (
              <div className="text-3xl font-semibold text-brand mb-8">
                {formatPrice(featured.regularPrice)}
              </div>
            )}
            <Link
              to={href}
              className="inline-flex items-center gap-x-2 uppercase tracking-wide font-semibold text-ink hover:text-brand transition-colors">
              Shop Now <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>

          {/* product image */}
          <div className="order-1 lg:order-2 flex justify-center">
            <img
              src={image}
              alt={title}
              className="w-full max-w-[520px] object-contain drop-shadow-2xl"
            />
          </div>
        </div>
      </div>

      {/* scroll cue → jumps to the product sections */}
      <div className="flex justify-center pb-8">
        <a href="#shop" aria-label="Scroll to products" className="text-muted hover:text-brand transition-colors">
          <FiChevronDown className="text-2xl animate-bounce" />
        </a>
      </div>
    </section>
  );
}
